import React, { useEffect } from 'react'
import { useWeb3React } from '@web3-react/core'
import useSWR from 'swr'
import { Pool } from '@uniswap/v3-sdk'
import { Token as UniToken } from '@uniswap/sdk-core'
import Tooltip from './components/Tooltip/Tooltip'

import { ethers } from 'ethers'

import { getTokens, getWhitelistedTokens } from './data/Tokens'
import { getFeeHistory } from './data/Fees'

import {
  fetcher,
  formatAmount,
  formatArrayAmount,
  formatKeyAmount,
  parseValue,
  getInfoTokens,
  expandDecimals,
  bigNumberify,
  numberWithCommas,
  formatDate,
  getServerUrl,
  USD_DECIMALS,
  GMX_DECIMALS,
  GLP_DECIMALS,
  BASIS_POINTS_DIVISOR
} from './Helpers'

import { getContract } from './Addresses'

import ReaderV2 from './abis/ReaderV2.json'
import GlpManager from './abis/GlpManager.json'
import UniPool from './abis/UniPool.json'
import Token from './abis/Token.json'

import Footer from "./Footer"

import "./DashboardV2.css"

const { AddressZero } = ethers.constants

function getTotalVolumeSum(volumes) {
  if (!volumes || volumes.length === 0) {
    return
  }

  let volume = bigNumberify(0)
  for (let i = 0; i < volumes.length; i++) {
    volume = volume.add(volumes[i].data.volume)
  }

  return volume
}

function getVolumeInfo(dailyVolume) {
  if (!dailyVolume || dailyVolume.length === 0) {
    return {}
  }

  const timestamp = dailyVolume[0].data.timestamp

  const info = {}
  let totalVolume = bigNumberify(0)
  for (let i = 0; i < dailyVolume.length; i++) {
    const item = dailyVolume[i].data
    if (item.timestamp !== timestamp) {
      break
    }

    if (!info[item.token]) {
      info[item.token] = bigNumberify(0)
    }

    info[item.token] = info[item.token].add(item.volume)
    totalVolume = totalVolume.add(item.volume)
  }

  info.totalVolume = totalVolume

  return info
}

function getCurrentFeesUsd(tokenAddresses, fees, infoTokens) {
  if (!fees || !infoTokens) {
    return bigNumberify(0)
  }

  let currentFeesUsd = bigNumberify(0)
  for (let i = 0; i < tokenAddresses.length; i++) {
    const tokenAddress = tokenAddresses[i]
    const tokenInfo = infoTokens[tokenAddress]
    if (!tokenInfo || !tokenInfo.minPrice) {
      continue
    }

    const feeUsd = fees[i].mul(tokenInfo.minPrice).div(expandDecimals(1, tokenInfo.decimals))
    currentFeesUsd = currentFeesUsd.add(feeUsd)
  }

  return currentFeesUsd
}

export default function DashboardV2() {
  const { active, library } = useWeb3React()
  const chainId = 42161 // set chain to Arbitrum

  const positionStatsUrl = getServerUrl(chainId, "/position_stats")
  const { data: positionStats, mutate: updatePositionStats } = useSWR([positionStatsUrl], {
    fetcher: (...args) => fetch(...args).then(res => res.json())
  })

  const dailyVolumeUrl = getServerUrl(chainId, "/daily_volume")
  const { data: dailyVolume, mutate: updateDailyVolume } = useSWR([dailyVolumeUrl], {
    fetcher: (...args) => fetch(...args).then(res => res.json())
  })

  const totalVolumeUrl = getServerUrl(chainId, "/total_volume")
  const { data: totalVolume, mutate: updateTotalVolume } = useSWR([totalVolumeUrl], {
    fetcher: (...args) => fetch(...args).then(res => res.json())
  })

  let totalLongPositionSizes
  let totalShortPositionSizes
  if (positionStats && positionStats.totalLongPositionSizes && positionStats.totalShortPositionSizes) {
    totalLongPositionSizes = bigNumberify(positionStats.totalLongPositionSizes)
    totalShortPositionSizes = bigNumberify(positionStats.totalShortPositionSizes)
  }

  const volumeInfo = getVolumeInfo(dailyVolume)

  const totalVolumeSum = getTotalVolumeSum(totalVolume)

  const tokens = getTokens(chainId)
  const whitelistedTokens = getWhitelistedTokens(chainId)
  const whitelistedTokenAddresses = whitelistedTokens.map(token => token.address)
  const tokenList = whitelistedTokens.filter(t => !t.isWrapped)

  const readerAddress = getContract(chainId, "Reader")
  const vaultAddress = getContract(chainId, "Vault")
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN")
  const glpManagerAddress = getContract(chainId, "GlpManager")

  const gmxAddress = getContract(chainId, "GMX")
  const glpAddress = getContract(chainId, "GLP")

  const tokensForSupplyQuery = [gmxAddress, glpAddress]

  const { data: aums, mutate: updateAums } = useSWR([`Dashboard:getAums:${active}`, chainId, glpManagerAddress, "getAums"], {
    fetcher: fetcher(library, GlpManager),
  })

  const { data: vaultTokenInfo, mutate: updateVaultTokenInfo } = useSWR([`Dashboard:vaultTokenInfo:${active}`, chainId, readerAddress, "getVaultTokenInfo"], {
    fetcher: fetcher(library, ReaderV2, [vaultAddress, nativeTokenAddress, expandDecimals(1, 18), whitelistedTokenAddresses]),
  })

  const { data: fees, mutate: updateFees } = useSWR([`Dashboard:fees:${active}`, chainId, readerAddress, "getFees", vaultAddress], {
    fetcher: fetcher(library, ReaderV2, [whitelistedTokenAddresses]),
  })

  const { data: totalSupplies, mutate: updateTotalSupplies } = useSWR([`Dashboard:totalSupplies:${active}`, chainId, readerAddress, "getTokenBalancesWithSupplies", AddressZero], {
    fetcher: fetcher(library, ReaderV2, [tokensForSupplyQuery]),
  })

  const poolAddress = "0x80A9ae39310abf666A87C743d6ebBD0E8C42158E" // GMX/WETH

  const { data: uniPoolSlot0, mutate: updateUniPoolSlot0 } = useSWR([`StakeV2:uniPoolSlot0:${active}`, chainId, poolAddress, "slot0"], {
    fetcher: fetcher(library, UniPool),
  })

  const stakedGmxTrackerAddress = getContract(chainId, "StakedGmxTracker")

  const { data: stakedGmxSupply, mutate: updateStakedGmxSupply } = useSWR(["StakeV2:stakedGmxSupply", chainId, gmxAddress, "balanceOf", stakedGmxTrackerAddress], {
    fetcher: fetcher(library, Token),
  })

  const infoTokens = getInfoTokens(tokens, undefined, whitelistedTokens, vaultTokenInfo, undefined)

  const eth = infoTokens[nativeTokenAddress]
  const currentFeesUsd = getCurrentFeesUsd(whitelistedTokenAddresses, fees, infoTokens)

  const feeHistory = getFeeHistory(chainId)
  const shouldIncludeCurrrentFees = (parseInt(Date.now() / 1000) - feeHistory[0].to) > 60 * 60
  let totalFeesDistributed = shouldIncludeCurrrentFees ? parseFloat(bigNumberify(formatAmount(currentFeesUsd, USD_DECIMALS - 2, 0, false)).toNumber()) / 100 : 0
  for (let i = 0; i < feeHistory.length; i++) {
    totalFeesDistributed += parseFloat(feeHistory[i].feeUsd)
  }

  const busdFloorPriceFundUsd = expandDecimals(1000000, 30)
  const ethFloorPriceFund = expandDecimals(800 + 270, 18)

  let ethFloorPriceFundUsd
  let totalFloorPriceFundUsd

  if (eth && eth.minPrice) {
    ethFloorPriceFundUsd = ethFloorPriceFund.mul(eth.minPrice).div(expandDecimals(1, eth.decimals))
    totalFloorPriceFundUsd = busdFloorPriceFundUsd.add(ethFloorPriceFundUsd)
  }

  let gmxPrice

  if (uniPoolSlot0 && eth && eth.minPrice) {
    const tokenA = new UniToken(chainId, nativeTokenAddress, 18, "SYMBOL", "NAME")
    const tokenB = new UniToken(chainId, gmxAddress, 18, "SYMBOL", "NAME")

    const pool = new Pool(
      tokenA, // tokenA
      tokenB, // tokenB
      10000, // fee
      uniPoolSlot0.sqrtPriceX96, // sqrtRatioX96
      1, // liquidity
      uniPoolSlot0.tick, // tickCurrent
      []
    )

    const poolTokenPrice = pool.priceOf(tokenB).toSignificant(6)
    const poolTokenPriceAmount = parseValue(poolTokenPrice, 18)
    gmxPrice = poolTokenPriceAmount.mul(eth.minPrice).div(expandDecimals(1, 18))
  }

  let gmxMarketCap
  if (gmxPrice && totalSupplies && totalSupplies[1]) {
    gmxMarketCap = gmxPrice.mul(totalSupplies[1]).div(expandDecimals(1, GMX_DECIMALS))
  }

  let aum
  if (aums && aums.length > 0) {
    aum = aums[0].add(aums[1]).div(2)
  }

  let tvl
  if (aum && gmxPrice && stakedGmxSupply) {
    tvl = aum.add(gmxPrice.mul(stakedGmxSupply).div(expandDecimals(1, GMX_DECIMALS)))
  }

  let glpPrice
  let glpSupply
  let glpMarketCap
  if (aum && totalSupplies && totalSupplies[3]) {
    glpSupply = totalSupplies[3]
    glpPrice = (aum && aum.gt(0) && glpSupply.gt(0)) ? aum.mul(expandDecimals(1, GLP_DECIMALS)).div(glpSupply) : expandDecimals(1, USD_DECIMALS)
    glpMarketCap = glpPrice.mul(glpSupply).div(expandDecimals(1, GLP_DECIMALS))
  }

  const hourValue = parseInt((new Date() - new Date().setUTCHours(0,0,0,0)) / (60 * 60 * 1000))
  const minuteValue = parseInt((new Date() - new Date().setUTCHours(0,0,0,0)) / (60 * 1000))
  let volumeLabel = hourValue > 0 ? `${hourValue}h` : `${minuteValue}m`

  useEffect(() => {
    if (active) {
      library.on('block', () => {
        updatePositionStats(undefined, true)
        updateDailyVolume(undefined, true)
        updateTotalVolume(undefined, true)

        updateTotalSupplies(undefined, true)
        updateAums(undefined, true)
        updateVaultTokenInfo(undefined, true)

        updateFees(undefined, true)
        updateUniPoolSlot0(undefined, true)
        updateStakedGmxSupply(undefined, true)
      })
      return () => {
        library.removeAllListeners('block')
      }
    }
  }, [active, library,  chainId,
      updatePositionStats, updateDailyVolume, updateTotalVolume,
      updateTotalSupplies, updateAums, updateVaultTokenInfo,
      updateFees, updateUniPoolSlot0, updateStakedGmxSupply])

  return (
    <div className="DashboardV2 Page">
      <div className="Page-title-section">
        <div className="Page-title">Stats</div>
        <div className="Page-description">
          Total Stats start from 01 Sep 2021.&nbsp;
          View BSC stats on <a href="https://gambit.financial/dashboard"  target="_blank" rel="noopener noreferrer">Gambit</a>.
        </div>
      </div>
      <div className="DashboardV2-content">
        <div className="DashboardV2-cards">
          <div className="App-card">
            <div className="App-card-title">Overview</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">AUM</div>
                <div>
                  <Tooltip handle={`$${formatAmount(tvl, USD_DECIMALS, 0, true)}`} position="right-bottom">
                    Assets Under Management: GLP pool + GMX staked
                  </Tooltip>
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">GLP Pool</div>
                <div>
                  <Tooltip handle={`$${formatAmount(aum, USD_DECIMALS, 0, true)}`} position="right-bottom">
                    Total value of tokens in GLP pool
                  </Tooltip>
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">{volumeLabel} Volume</div>
                <div>
                  ${formatAmount(volumeInfo.totalVolume, USD_DECIMALS, 0, true)}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Long Positions</div>
                <div>
                  ${formatAmount(totalLongPositionSizes, USD_DECIMALS, 0, true)}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Short Positions</div>
                <div>
                  ${formatAmount(totalShortPositionSizes, USD_DECIMALS, 0, true)}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Fees since {formatDate(feeHistory[0].to)}</div>
                <div>
                  ${formatAmount(currentFeesUsd, USD_DECIMALS, 2, true)}
                </div>
              </div>
            </div>
          </div>
          <div className="App-card">
            <div className="App-card-title">Total Stats</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">Total Fees</div>
                <div>
                  ${numberWithCommas(totalFeesDistributed.toFixed(0))}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Total Volume</div>
                <div>
                  ${formatAmount(totalVolumeSum, USD_DECIMALS, 0, true)}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Floor Price Fund</div>
                <div>
                  ${formatAmount(totalFloorPriceFundUsd, 30, 0, true)}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="Page-title-section">
          <div className="Page-title">Tokens</div>
          <div className="Page-description">
            Platform and GLP index tokens.
          </div>
        </div>
        <div className="DashboardV2-token-cards">
          <div className="App-card">
            <div className="App-card-title">GMX</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">Price</div>
                <div>
                  ${formatAmount(gmxPrice, USD_DECIMALS, 2, true)}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Supply</div>
                <div>
                  {formatArrayAmount(totalSupplies, 1, GMX_DECIMALS, 0, true)} GMX
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Market Cap</div>
                <div>
                  ${formatAmount(gmxMarketCap, USD_DECIMALS, 0, true)}
                </div>
              </div>
            </div>
          </div>
          <div className="App-card">
            <div className="App-card-title">GLP</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">Price</div>
                <div>
                  ${formatAmount(glpPrice, USD_DECIMALS, 2, true)}
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Supply</div>
                <div>
                  {formatArrayAmount(totalSupplies, 3, GMX_DECIMALS, 0, true)} GLP
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Market Cap</div>
                <div>
                  ${formatAmount(glpMarketCap, USD_DECIMALS, 0, true)}
                </div>
              </div>
            </div>
          </div>
          {tokenList.map((token) => {
            const tokenInfo = infoTokens[token.address]
            let utilization = bigNumberify(0)
            if (tokenInfo && tokenInfo.reservedAmount && tokenInfo.poolAmount && tokenInfo.poolAmount.gt(0)) {
              utilization = tokenInfo.reservedAmount.mul(BASIS_POINTS_DIVISOR).div(tokenInfo.poolAmount)
            }

            return (
              <div className="App-card" key={token.symbol}>
                <div className="App-card-title">{token.symbol}</div>
                <div className="App-card-divider"></div>
                <div className="App-card-content">
                  <div className="App-card-row">
                    <div className="label">Price</div>
                    <div>
                      ${formatKeyAmount(tokenInfo, "minPrice", USD_DECIMALS, 2, true)}
                    </div>
                  </div>
                  <div className="App-card-row">
                    <div className="label">Pool</div>
                    <div>
                      {formatKeyAmount(tokenInfo, "managedAmount", token.decimals, 2, true)} {token.symbol} (${formatKeyAmount(tokenInfo, "managedUsd", USD_DECIMALS, 0, true)})
                    </div>
                  </div>
                  <div className="App-card-row">
                    <div className="label">Utilization</div>
                    <div>
                      {formatAmount(utilization, 2, 2, false)}%
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        <div className="Page-title-section">
          <div className="Page-title">Community Projects</div>
          <div className="Page-description">
            Projects developed by the GMX community.
          </div>
        </div>
        <div className="DashboardV2-projects">
          <div className="App-card">
            <div className="App-card-title">GMX Positions Bot</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">Link</div>
                <div>
                  <a href="https://t.me/GMXPositions" target="_blank" rel="noopener noreferrer">
                    https://t.me/GMXPositions
                  </a>
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">About</div>
                <div>
                  Telegram bot for GMX position updates
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Creator</div>
                <div>
                  <a href="https://t.me/zhongfu" target="_blank" rel="noopener noreferrer">
                    @zhongfu
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="App-card">
            <div className="App-card-title">GMX Leaderboard</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">Link</div>
                <div>
                  <a href="https://www.gmx.house/" target="_blank" rel="noopener noreferrer">
                    https://www.gmx.house
                  </a>
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">About</div>
                <div>
                  Leaderboard for GMX traders
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Creator</div>
                <div>
                  <a href="https://t.me/Itburnz" target="_blank" rel="noopener noreferrer">
                    @Itburnz
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="App-card">
            <div className="App-card-title">Unofficial GMX Yield List</div>
            <div className="App-card-divider"></div>
            <div className="App-card-content">
              <div className="App-card-row">
                <div className="label">Link</div>
                <div>
                  <a href="https://gist.github.com/gmxyield/3c2941634ce42cf24b57bdba6f99067f" target="_blank" rel="noopener noreferrer">
                    https://gist.github.com/gmxyield
                  </a>
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">About</div>
                <div>
                  List of rewards from external projects
                </div>
              </div>
              <div className="App-card-row">
                <div className="label">Creator</div>
                <div>
                  <a href="https://gist.github.com/gmxyield" target="_blank" rel="noopener noreferrer">
                    @gmxyield
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}
